import React, { useCallback, useEffect, useState } from 'react';
import { jornadaControleAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Timer, Loader2, CheckCircle2, XCircle, Hourglass } from 'lucide-react';

const fmtHoras = (h) => {
  const n = Number(h) || 0;
  const hh = Math.floor(n);
  const mm = Math.round((n - hh) * 60);
  return mm ? `${hh}h${String(mm).padStart(2, '0')}` : `${hh}h`;
};
const fmtData = (d) => (d ? d.split('-').reverse().join('/') : '-');
const fmtHora = (iso) => (iso ? String(iso).slice(11, 16) : '');

const SITUACAO = {
  aprovada: { label: 'Aprovada', cls: 'bg-emerald-100 text-emerald-700', Icone: CheckCircle2 },
  negada: { label: 'Negada', cls: 'bg-red-100 text-red-700', Icone: XCircle },
  pendente: { label: 'Pendente', cls: 'bg-amber-100 text-amber-700', Icone: Hourglass },
};

export const HorasExtrasMes = ({ userId, colaboradores = [] }) => {
  const [mes, setMes] = useState(new Date().toISOString().slice(0, 7));
  const [status, setStatus] = useState('');
  const [dados, setDados] = useState({ itens: [], resumo: [], total_horas_aprovadas: 0 });
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [ano, m] = mes.split('-').map(Number);
      const ultimo = new Date(ano, m, 0).getDate();
      const res = await jornadaControleAPI.horasExtrasPeriodo({
        data_inicio: `${mes}-01`, data_fim: `${mes}-${String(ultimo).padStart(2, '0')}`,
        user_id: userId || undefined, status: status || undefined,
      });
      setDados(res.data);
    } catch (e) {
      setDados({ itens: [], resumo: [], total_horas_aprovadas: 0 });
    } finally {
      setLoading(false);
    }
  }, [mes, userId, status]);

  useEffect(() => { carregar(); }, [carregar]);

  const nomeColab = (i) => i.user_nome || colaboradores.find((c) => c.id === i.user_id)?.nome || '-';

  return (
    <Card data-testid="horas-extras-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Timer className="h-5 w-5 text-amber-600" /> Horas extras do mês
            <span className="font-mono text-amber-700 ml-2" data-testid="horas-extras-total">Total aprovado: {fmtHoras(dados.total_horas_aprovadas)}</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="w-44" data-testid="horas-extras-mes" />
            <Select value={status || 'todas'} onValueChange={(v) => setStatus(v === 'todas' ? '' : v)}>
              <SelectTrigger className="w-36" data-testid="horas-extras-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="aprovada">Aprovadas</SelectItem>
                <SelectItem value="negada">Negadas</SelectItem>
                <SelectItem value="pendente">Pendentes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-amber-600" /></div>
        ) : dados.itens.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8" data-testid="horas-extras-vazio">Nenhuma hora extra registrada neste mês.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="horas-extras-resumo">
              {dados.resumo.map((r) => (
                <div key={r.user_id} className="rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-900/15 px-3 py-2 text-xs" data-testid={`horas-extras-resumo-${r.user_id}`}>
                  <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{r.user_nome} <span className="text-[10px] text-gray-400 font-normal">{r.perfil}</span></p>
                  <p className="text-amber-800 dark:text-amber-300 font-mono font-bold">{fmtHoras(r.horas_aprovadas)} aprovadas</p>
                  <p className="text-gray-500 dark:text-gray-400">{r.pedidos} pedido{r.pedidos > 1 ? 's' : ''} · {r.aprovadas} ok · {r.negadas} negada{r.negadas === 1 ? '' : 's'}{r.pendentes ? ` · ${r.pendentes} pendente` : ''}</p>
                </div>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="horas-extras-tabela">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2 font-semibold">Data</th>
                    <th className="text-left p-2 font-semibold">Colaborador</th>
                    <th className="text-left p-2 font-semibold">Pedido</th>
                    <th className="text-center p-2 font-semibold">Situação</th>
                    <th className="text-center p-2 font-semibold">Liberado</th>
                    <th className="text-left p-2 font-semibold">Decidido por</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.itens.map((i) => {
                    const s = SITUACAO[i.status] || SITUACAO.pendente;
                    const decisao = i.hora_aprovacao || i.hora_negacao;
                    return (
                      <tr key={`${i.user_id}-${i.data}`} className="border-b hover:bg-gray-50" data-testid={`horas-extras-row-${i.user_id}-${i.data}`}>
                        <td className="p-2 font-mono">{fmtData(i.data)}</td>
                        <td className="p-2">{nomeColab(i)}<span className="block text-[10px] text-gray-400">{i.perfil || ''}</span></td>
                        <td className="p-2">
                          <span className="font-mono">{fmtHoras(i.horas_solicitadas || 1)}</span>
                          {i.motivo && <span className="block text-xs text-gray-500 max-w-[260px] truncate" title={i.motivo}>“{i.motivo}”</span>}
                          <span className="block text-[10px] text-gray-400">pedido às {fmtHora(i.hora_solicitacao)}</span>
                        </td>
                        <td className="p-2 text-center"><Badge className={`${s.cls} text-xs`}><s.Icone className="w-3 h-3 mr-1" />{s.label}</Badge></td>
                        <td className="p-2 text-center font-mono">{i.status === 'aprovada' ? `${fmtHoras(i.horas)} · até ${i.hora_limite}` : '-'}</td>
                        <td className="p-2">
                          {i.admin_nome ? (
                            <>
                              <span className="font-medium">{i.admin_nome}</span>
                              <span className="block text-[10px] text-gray-400">{i.aprovado_por_perfil || 'ADMIN'}{decisao ? ` · ${fmtHora(decisao)}` : ''}</span>
                            </>
                          ) : <span className="text-gray-400 text-xs">aguardando</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default HorasExtrasMes;
